/**
 * Collection Controls — Dutton Brown
 *
 * Adds two collection-page controls:
 *   1. <collection-filter-toggle>   Hide/show the sidebar facets column.
 *                                   Persists choice in localStorage so it
 *                                   carries across reloads and collections.
 *   2. <collection-density>         Lets the customer pick 2/3/4-wide grid
 *                                   on desktop or 1/2-wide on mobile.
 *                                   Persists per breakpoint in localStorage.
 *
 * Both controls live in collection-top-bar.liquid; the CSS lives in
 * custom.css under the "/* COLLECTION CONTROLS" section.
 */

(() => {
  const LS_FILTERS_HIDDEN = "db_filters_hidden";
  const LS_DENSITY_DESKTOP = "db_grid_density_desktop";
  const LS_DENSITY_MOBILE = "db_grid_density_mobile";

  // -------------------------------------------------------------------- //
  // Filter visibility toggle
  // -------------------------------------------------------------------- //
  class CollectionFilterToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector("button");
      this.label = this.querySelector(".collection-controls__toggle-label");
      this.collection = document.querySelector(".collection");

      // Single permanent instance — label flips between "Hide filters" /
      // "Show filters". Element never hides/moves; only its text changes.
      this._applyState(localStorage.getItem(LS_FILTERS_HIDDEN) === "1");
      this.button.addEventListener("click", () => {
        const next = !this.collection.classList.contains(
          "collection--filters-hidden"
        );
        this._applyState(next);
        localStorage.setItem(LS_FILTERS_HIDDEN, next ? "1" : "0");
      });
    }

    _applyState(hidden) {
      if (!this.collection) return;
      this.collection.classList.toggle("collection--filters-hidden", hidden);
      this.button.setAttribute("aria-pressed", String(hidden));
      const text = hidden ? "Show filters" : "Hide filters";
      this.button.setAttribute("aria-label", text);
      if (this.label) this.label.textContent = text;
    }
  }

  if (!customElements.get("collection-filter-toggle")) {
    customElements.define("collection-filter-toggle", CollectionFilterToggle);
  }

  // -------------------------------------------------------------------- //
  // Grid density picker
  // -------------------------------------------------------------------- //
  class CollectionDensity extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll("[data-density]")];
      const mobile = this.hasAttribute("data-mobile");
      this.storageKey = mobile ? LS_DENSITY_MOBILE : LS_DENSITY_DESKTOP;
      this.cssVar = mobile
        ? "--product-list-items-per-row-mobile"
        : "--product-list-items-per-row-desktop";

      // First-time defaults: 4-wide on desktop, 2-wide on mobile.
      // After the user picks anything, their choice is persisted in
      // localStorage and used on every subsequent visit (across collections).
      const stored = localStorage.getItem(this.storageKey);
      const defaultDensity = mobile ? "2" : "4";
      const density = stored || defaultDensity;
      this._applyDensity(density, false);

      this.buttons.forEach((btn) =>
        btn.addEventListener("click", () => {
          const density = btn.dataset.density;
          this._applyDensity(density, true);
          localStorage.setItem(this.storageKey, density);
          // Sync sibling density pickers (sidebar + top-bar mirror)
          document.querySelectorAll("collection-density").forEach((other) => {
            if (other === this) return;
            const sameBreakpoint = other.hasAttribute("data-mobile") === mobile;
            if (sameBreakpoint && other._syncFromSibling) {
              other._syncFromSibling(density);
            }
          });
        })
      );
    }

    _syncFromSibling(density) {
      // Update aria-pressed only — don't re-set CSS var (already done by the
      // originating instance) or write localStorage (would loop).
      this.buttons.forEach((b) =>
        b.setAttribute(
          "aria-pressed",
          b.dataset.density === String(density) ? "true" : "false"
        )
      );
    }

    _highlightCurrent() {
      // Find the current --product-list-items-per-row from the section style
      const section = document.querySelector(".collection")?.closest("[id^='shopify-section-']");
      if (!section) return;
      const current = getComputedStyle(section).getPropertyValue(
        "--product-list-items-per-row"
      ).trim();
      const match = this.buttons.find((b) => b.dataset.density === current);
      if (match) match.setAttribute("aria-pressed", "true");
    }

    _applyDensity(density, animate) {
      this.buttons.forEach((b) =>
        b.setAttribute(
          "aria-pressed",
          b.dataset.density === String(density) ? "true" : "false"
        )
      );

      // Apply to the parent collection section's CSS variables
      const section = document.querySelector(".collection")?.closest("[id^='shopify-section-']");
      if (!section) return;

      // The base section sets --product-list-items-per-row dynamically by
      // breakpoint. We override it via an inline scope on the section, only
      // for the current breakpoint.
      const mobile = this.hasAttribute("data-mobile");
      const mediaQuery = window.matchMedia("(max-width: 699px)");
      const inMobile = mediaQuery.matches;

      if (mobile === inMobile) {
        section.style.setProperty(
          "--product-list-items-per-row",
          density
        );
      } else {
        // Save for when we cross breakpoint — set a custom attribute that
        // we'll re-read on resize
        section.dataset[mobile ? "densityMobile" : "densityDesktop"] = density;
      }

      if (animate) {
        section.classList.add("collection--density-changing");
        clearTimeout(this._t);
        this._t = setTimeout(
          () => section.classList.remove("collection--density-changing"),
          280
        );
      }
    }
  }

  if (!customElements.get("collection-density")) {
    customElements.define("collection-density", CollectionDensity);
  }

  // -------------------------------------------------------------------- //
  // Resize handler — re-apply density when the breakpoint changes
  // -------------------------------------------------------------------- //
  const mq = window.matchMedia("(max-width: 699px)");
  mq.addEventListener("change", () => {
    const section = document.querySelector(".collection")?.closest("[id^='shopify-section-']");
    if (!section) return;
    const inMobile = mq.matches;
    const key = inMobile ? LS_DENSITY_MOBILE : LS_DENSITY_DESKTOP;
    const stored = localStorage.getItem(key);
    if (stored) section.style.setProperty("--product-list-items-per-row", stored);
    else section.style.removeProperty("--product-list-items-per-row");
  });

  // -------------------------------------------------------------------- //
  // Body-portal tooltip for color/finish swatches in the sidebar.
  // The default Impact tooltip is a CSS pseudo-element on the swatch.
  // Inside a flex-wrap row, the tooltip on a row-N swatch overlaps with
  // row-(N-1) swatches in pixel space and gets clipped/hidden. Fix:
  // render the tooltip as a body-level <div> on hover, positioned via
  // getBoundingClientRect — no ancestor can clip it.
  // -------------------------------------------------------------------- //
  const tooltip = document.createElement("div");
  tooltip.className = "db-portal-tooltip";
  tooltip.setAttribute("aria-hidden", "true");
  tooltip.style.cssText = [
    "position:fixed",
    "pointer-events:none",
    "background:#4A4A4A",
    "color:#fff",
    "font:600 12px/1.2 Poppins, sans-serif",
    "padding:4px 10px",
    "border-radius:4px",
    "white-space:nowrap",
    "opacity:0",
    "transition:opacity .15s ease",
    "z-index:9999",
    "transform:translate(-50%, -100%)",
  ].join(";");
  document.body.appendChild(tooltip);

  function showTooltip(target) {
    const label = target.getAttribute("data-tooltip") || target.getAttribute("aria-label");
    if (!label) return;
    const rect = target.getBoundingClientRect();
    tooltip.textContent = label;
    tooltip.style.left = rect.left + rect.width / 2 + "px";
    tooltip.style.top = rect.top - 6 + "px";
    tooltip.style.opacity = "1";
  }
  function hideTooltip() {
    tooltip.style.opacity = "0";
  }

  // Bind via delegated mouseover/mouseout (these BUBBLE; pointerenter/leave do not).
  // We resolve the swatch by .closest() so nested labels/inputs don't break tracking.
  let currentSwatch = null;
  document.addEventListener("mouseover", (e) => {
    const sw = e.target.closest && e.target.closest(".color-filter .color-swatch[data-tooltip]");
    if (sw && sw !== currentSwatch) {
      currentSwatch = sw;
      showTooltip(sw);
    }
  });
  document.addEventListener("mouseout", (e) => {
    if (!currentSwatch) return;
    // Use relatedTarget to check whether the pointer is still inside the same swatch
    const rt = e.relatedTarget;
    if (rt && rt.closest && rt.closest(".color-filter .color-swatch[data-tooltip]") === currentSwatch) return;
    currentSwatch = null;
    hideTooltip();
  });
  // Hide tooltip on scroll (would otherwise float in stale position)
  window.addEventListener("scroll", () => { currentSwatch = null; hideTooltip(); }, { passive: true });

  // -------------------------------------------------------------------- //
  // "By SKU" custom sort
  // -------------------------------------------------------------------- //
  // Shopify can't sort by SKU server-side. When the user picks "By SKU"
  // in the sidebar Sort accordion, we:
  //   1. Intercept the radio change so Shopify doesn't trigger a real
  //      re-fetch with the unknown sort value.
  //   2. Read SKUs from every <product-card>'s data-sku attribute.
  //   3. Reorder the cards in DOM by SKU alphanumeric (low → high).
  //   4. Update URL state via history.pushState so the choice persists
  //      across reloads (the Liquid renderer reads results.sort_by which
  //      will still be the default; we re-apply via JS on load).
  // -------------------------------------------------------------------- //

  const BY_SKU_VALUE = "by-sku";

  function naturalSkuCompare(a, b) {
    // Split into numeric and non-numeric runs so "10A" sorts after "9A"
    // (not before it as a plain string compare would).
    const re = /(\d+)|(\D+)/g;
    const aParts = String(a || "").toUpperCase().match(re) || [];
    const bParts = String(b || "").toUpperCase().match(re) || [];
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const ap = aParts[i];
      const bp = bParts[i];
      if (ap === undefined) return -1;
      if (bp === undefined) return 1;
      const an = parseInt(ap, 10);
      const bn = parseInt(bp, 10);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) {
        if (an !== bn) return an - bn;
      } else if (ap !== bp) {
        return ap < bp ? -1 : 1;
      }
    }
    return 0;
  }

  function applySkuSort() {
    const list = document.querySelector(".product-list, product-list");
    if (!list) return;
    const cards = [...list.children].filter((c) =>
      /product-card|product-list__promo/i.test(c.tagName + " " + c.className)
    );
    // Sort by data-sku ascending. Cards without a SKU sink to the bottom.
    cards.sort((a, b) => {
      const sa = a.getAttribute("data-sku") || "";
      const sb = b.getAttribute("data-sku") || "";
      if (!sa && !sb) return 0;
      if (!sa) return 1;
      if (!sb) return -1;
      return naturalSkuCompare(sa, sb);
    });
    cards.forEach((c) => list.appendChild(c));
  }

  function isSkuSortActive() {
    const params = new URLSearchParams(window.location.search);
    return params.get("sort_by") === BY_SKU_VALUE;
  }

  // On page load, if URL says by-sku, reorder. Wait for DOM ready + a
  // microtask in case <product-list> is a custom element that initializes
  // after this script runs and re-orders its children.
  function scheduleInitialSort() {
    if (!isSkuSortActive()) return;
    // Try a few times to outlast the custom-element initialization
    requestAnimationFrame(() => {
      applySkuSort();
      setTimeout(applySkuSort, 100);
      setTimeout(applySkuSort, 400);
    });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleInitialSort);
  } else {
    scheduleInitialSort();
  }

  // Intercept clicks on the by-sku radio. The form is auto-submitted by
  // facet-form when an input changes, which would refetch the section
  // with sort_by=by-sku — Shopify will ignore that value and fall back
  // to default, but our JS will then re-sort. We just need to make sure
  // the URL ends up containing sort_by=by-sku so a refresh stays sticky.
  document.addEventListener("change", (e) => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.name !== "sort_by") return;
    if (input.value !== BY_SKU_VALUE) return;

    // Push our value into the URL so a reload keeps it.
    const url = new URL(window.location.href);
    url.searchParams.set("sort_by", BY_SKU_VALUE);
    window.history.replaceState({}, "", url.toString());
    // Run the sort after the facet-form's re-render finishes
    setTimeout(applySkuSort, 50);
  });

  // After Impact's facet form re-renders the grid (custom event), re-sort.
  document.addEventListener("shopify:section:load", () => {
    if (isSkuSortActive()) setTimeout(applySkuSort, 50);
  });
})();
